import type { ProductDetail, ProductDetailVariant } from '../types/api';

const DEFAULT_TITLE = 'Default Title';
const LABEL_MAX_LENGTH = 60;

/**
 * Human-readable label of a variant for a chat product card, e.g. "Blue / M".
 *
 * Same rule as Portal (`VeceDb::ProductVariant#variant_label`), the widget,
 * desktop-app and iOS, so a card reads the same whoever sent it:
 *  1. option values as the API returns them (trimmed, case kept), without
 *     blanks and "Default Title", joined with " / ";
 *  2. no values left — the variant's own name, unless blank or "Default Title";
 *  3. longer than 60 characters — cut to 59 plus "…".
 * Returns '' when there is nothing worth showing.
 */
export function variantLabel(variant: Pick<ProductDetailVariant, 'name' | 'options'>): string {
  const values = (variant.options ?? [])
    .map((option) => String(option?.value ?? '').trim())
    .filter((value) => value !== '' && value !== DEFAULT_TITLE);

  let label = values.join(' / ');
  if (label === '') {
    const name = (variant.name ?? '').trim();
    label = name === DEFAULT_TITLE ? '' : name;
  }

  return label.length > LABEL_MAX_LENGTH ? `${label.slice(0, LABEL_MAX_LENGTH - 1)}…` : label;
}

/** Card name: product name, plus " — <label>" when the variant has a label. */
export function variantCardName(
  productName: string,
  variant: Pick<ProductDetailVariant, 'name' | 'options'>
): string {
  const label = variantLabel(variant);
  return label === '' ? productName : `${productName} — ${label}`;
}

/** One product card as sent in a chat message's `json` attribute. */
export interface ProductCardPayload {
  id: number;
  variant_id?: number;
  name: string;
  price: string;
  image: string;
  url: string | null;
  compare_at_price: string | null;
}

/**
 * Build the chat card for a product shared from the PDP.
 *
 * With a variant — the card of that variant: its id, price, old price and
 * image (the product's first image when the variant has none), and the name
 * with the variant label. Without one (the selected options match no
 * variant) — the product card, as before.
 */
export function buildProductCard(
  product: ProductDetail,
  variant: ProductDetailVariant | null
): ProductCardPayload {
  const productName = product.title || product.name || '';
  const productImage = product.images?.[0] || product.image_url || '';

  if (!variant) {
    return {
      id: product.id,
      name: productName,
      price: product.price || product.product_variants?.[0]?.price || '',
      image: productImage,
      url: product.url || null,
      compare_at_price: product.compare_at_price || null,
    };
  }

  return {
    id: product.id,
    variant_id: variant.id,
    name: variantCardName(productName, variant),
    price: variant.price || '',
    image: variant.image || productImage,
    url: product.url || null,
    compare_at_price: variant.compare_at_price || null,
  };
}
